/**
 * Shared wallet-core config + non-React core handle.
 *
 * Single source of truth for the `WalletCoreConfig` that both the React
 * provider (`providers/WalletProvider.tsx`) and non-React call sites use,
 * so every `WalletCore` instance opens the same IndexedDB / WebAuthn RP.
 */

import {
  createWalletCore,
  type WalletCore,
  type WalletCoreConfig,
} from "@nullaone/wallet-core";

/**
 * Resolve the WebAuthn rpId for this deployment.
 *
 * WebAuthn requires the rpId to equal the current document's hostname OR be
 * a registrable parent of it. Mirrors betmebro/oracle: localhost (and any
 * dev hostname) stays itself. Add an apex-domain mapping here at public
 * launch (e.g. `*.detrust.ai` → `"detrust.ai"`) so credentials work across
 * subdomains.
 */
export function resolveRpId(): string {
  if (typeof window === "undefined") return "localhost";
  return window.location.hostname;
}

export const walletCoreConfig: WalletCoreConfig = {
  webauthn: { rpId: resolveRpId(), rpName: "DeTrust AI" },
};

let _core: WalletCore | null = null;

/**
 * Non-React access to a `WalletCore` (session bookkeeping, storage reads).
 *
 * Storage/local-wallet only — Ledger WebHID transport state must stay on
 * the provider-owned instance created by `<WalletReactProvider>`, so do
 * NOT call `core.ledger.connect*` from here. Storage operations are safe
 * to share: both instances open the same IndexedDB (same `dbName`).
 */
export function getWalletCore(): WalletCore {
  if (!_core) _core = createWalletCore(walletCoreConfig);
  return _core;
}
